import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Connection, PublicKey } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { getSolanaEndpoint } from '@/lib/solana/config';
import { getAssetBatch, extractAssetMetadata } from '@/lib/rpc/dasClient';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryClient';

export interface WalletToken {
  mint: string;
  symbol: string;
  name: string;
  imageUrl: string | null;
  balance: string;
  decimals: number;
  uiAmount: number;
  uiAmountString: string;
  programId: string;
}

export interface UseTokenBalancesResult {
  tokens: WalletToken[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

interface ParsedTokenAccount {
  mint: string;
  state: string;
  programId: string;
  tokenAmount: {
    amount: string;
    decimals: number;
    uiAmount: number | null;
    uiAmountString: string;
  };
}

// Shared connection for token fetching
let sharedConnection: Connection | null = null;
function getSharedConnection(): Connection {
  if (!sharedConnection) {
    sharedConnection = new Connection(getSolanaEndpoint(), 'confirmed');
  }
  return sharedConnection;
}

async function fetchTokenAccounts(ownerPubkey: PublicKey): Promise<ParsedTokenAccount[]> {
  const connection = getSharedConnection();

  const [tokenAccounts, token2022Accounts] = await Promise.all([
    connection.getParsedTokenAccountsByOwner(ownerPubkey, {
      programId: TOKEN_PROGRAM_ID,
    }),
    connection.getParsedTokenAccountsByOwner(ownerPubkey, {
      programId: TOKEN_2022_PROGRAM_ID,
    }),
  ]);

  const taggedTokenAccounts = tokenAccounts.value.map((a) => ({ ...a, ownerProgram: TOKEN_PROGRAM_ID.toBase58() }));
  const taggedToken2022Accounts = token2022Accounts.value.map((a) => ({ ...a, ownerProgram: TOKEN_2022_PROGRAM_ID.toBase58() }));
  const allAccounts = [...taggedTokenAccounts, ...taggedToken2022Accounts];

  return allAccounts
    .map((account) => {
      const parsed = account.account.data.parsed?.info;
      if (!parsed) return null;
      return {
        mint: parsed.mint as string,
        state: parsed.state as string,
        programId: account.ownerProgram,
        tokenAmount: parsed.tokenAmount as ParsedTokenAccount['tokenAmount'],
      };
    })
    .filter((a): a is ParsedTokenAccount => a !== null);
}

async function resolveMetadata(
  accounts: ParsedTokenAccount[]
): Promise<WalletToken[]> {
  const mintAddresses = accounts.map((a) => a.mint);
  if (mintAddresses.length === 0) return [];

  // Tier 1: Supabase launched_tokens
  const metadataMap = new Map<string, { name: string; symbol: string; imageUrl: string | null }>();

  try {
    const { data: launchedTokens } = await supabase
      .from('launched_tokens')
      .select('mint_address, name, symbol, image_url')
      .in('mint_address', mintAddresses);

    if (launchedTokens) {
      for (const token of launchedTokens) {
        metadataMap.set(token.mint_address, {
          name: token.name || '',
          symbol: token.symbol || '',
          imageUrl: token.image_url || null,
        });
      }
    }
  } catch (err) {
    console.warn('[useTokenBalances] Supabase lookup failed:', err);
  }

  // Tier 2: DAS for unresolved mints
  const unresolvedMints = mintAddresses.filter((m) => !metadataMap.has(m));

  if (unresolvedMints.length > 0) {
    try {
      const assetMap = await getAssetBatch(unresolvedMints);
      for (const mint of unresolvedMints) {
        const asset = assetMap.get(mint);
        if (asset) {
          const meta = extractAssetMetadata(asset);
          if (meta.name || meta.symbol) {
            metadataMap.set(mint, {
              name: meta.name || '',
              symbol: meta.symbol || '',
              imageUrl: meta.imageUrl || null,
            });
          }
        }
      }
    } catch (err) {
      console.warn('[useTokenBalances] DAS batch failed:', err);
    }
  }

  // Build final token list with Tier 3 fallback for remaining unresolved
  return accounts.map((account) => {
    const meta = metadataMap.get(account.mint);
    return {
      mint: account.mint,
      symbol: meta?.symbol || '???',
      name: meta?.name || `${account.mint.slice(0, 4)}...${account.mint.slice(-4)}`,
      imageUrl: meta?.imageUrl || null,
      balance: account.tokenAmount.amount,
      decimals: account.tokenAmount.decimals,
      uiAmount: account.tokenAmount.uiAmount ?? 0,
      uiAmountString: account.tokenAmount.uiAmountString,
      programId: account.programId,
    };
  });
}

async function fetchWalletTokens(ownerPubkey: PublicKey): Promise<WalletToken[]> {
  const accounts = await fetchTokenAccounts(ownerPubkey);

  // Filter frozen and zero-balance accounts
  const activeAccounts = accounts.filter(
    (a) => a.state !== 'frozen' && a.tokenAmount.amount !== '0'
  );

  const tokens = await resolveMetadata(activeAccounts);

  // Sort by raw balance descending (compare as BigInt for precision)
  tokens.sort((a, b) => {
    const balA = BigInt(a.balance);
    const balB = BigInt(b.balance);
    if (balB > balA) return 1;
    if (balB < balA) return -1;
    return 0;
  });

  return tokens;
}

export const useTokenBalances = (): UseTokenBalancesResult => {
  const { publicKey, connected, walletAddress, onWalletChange } = useTurnkeyWallet();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.wallet.tokenBalances(walletAddress || ''),
    queryFn: () => {
      if (!publicKey) throw new Error('No public key');
      return fetchWalletTokens(publicKey);
    },
    enabled: connected && !!publicKey && !!walletAddress,
    staleTime: 0, // Tier 1: real-time
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  // Subscribe to wallet change events for immediate invalidation
  useEffect(() => {
    if (!onWalletChange) return;
    const unsubscribe = onWalletChange(() => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.wallet.tokenBalances(walletAddress || ''),
      });
    });
    return unsubscribe;
  }, [onWalletChange, walletAddress, queryClient]);

  return useMemo(() => ({
    tokens: data ?? [],
    isLoading: isLoading && !data,
    error: error instanceof Error ? error.message : null,
    refetch,
  }), [data, isLoading, error, refetch]);
};
